// ** React Import
import { ReactNode, useEffect } from 'react';

// ** Next Import
import Link from 'next/link';

// ** MUI Imports
import IconButton from '@mui/material/IconButton';
import Box, { BoxProps } from '@mui/material/Box';
import { styled } from '@mui/material/styles';

// ** Icons
import Close from 'mdi-material-ui/Close';
import CircleOutline from 'mdi-material-ui/CircleOutline';
import RecordCircleOutline from 'mdi-material-ui/RecordCircleOutline';

// ** Type Import
import { Settings } from 'src/@core/context/settingsContext';
import { Products, useGetProductLevel } from 'src/@core/utils/getProductLevel';

import { useAuth } from 'react-oidc-context';
import { VigoTypography } from 'src/@vigo/vigo-typography';

interface Props {
  hidden: boolean;
  navHover: boolean;
  settings: Settings;
  collapsedNavWidth: number;
  menuLockedIcon?: ReactNode;
  menuUnlockedIcon?: ReactNode;
  navigationBorderWidth: number;
  toggleNavVisibility: () => void;
  saveSettings: (values: Settings) => void;
  verticalNavMenuBranding?: (props?: any) => ReactNode;
}

// ** Styled Components
const MenuHeaderWrapper = styled(Box)<BoxProps>(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingRight: theme.spacing(4.5),
  transition: 'padding .25s ease-in-out',
  minHeight: theme.mixins.toolbar.minHeight
}));

const StyledLink = styled('a')({
  display: 'flex',
  alignItems: 'center',
  textDecoration: 'none'
});

const VerticalNavHeader = (props: Props) => {
  // ** Props
  const {
    hidden,
    navHover,
    settings,
    saveSettings,
    collapsedNavWidth,
    toggleNavVisibility,
    navigationBorderWidth,
    menuLockedIcon: userMenuLockedIcon,
    menuUnlockedIcon: userMenuUnlockedIcon,
    verticalNavMenuBranding: userVerticalNavMenuBranding
  } = props;

  const prod = useGetProductLevel();

  const auth = useAuth();
  const company = auth.user?.profile.company;

  // ** Vars
  const { navCollapsed } = settings;

  const menuCollapsedStyles = navCollapsed && !navHover ? { opacity: 0 } : { opacity: 1 };

  function getFaviconEl() {
    return document.getElementById('favicon') as HTMLLinkElement | null;
  }

  function title(): string {
    switch (prod) {
      case Products.Fusion:
        return 'FUSION';

      case Products.Flow:
        return 'FLOW';

      case Products.MyPortal:
        return company as string;

      default:
        return 'QUANTUM';
    }
  }

  const menuHeaderPaddingLeft = () => {
    if (navCollapsed && !navHover) {
      if (userVerticalNavMenuBranding) {
        return 0;
      } else {
        return (collapsedNavWidth - navigationBorderWidth - 25) / 8;
      }
    } else {
      return 4;
    }
  };

  const MenuLockedIcon = () =>
    userMenuLockedIcon || (
      <RecordCircleOutline
        sx={{
          fontSize: '1.25rem',
          pointerEvents: 'none',
          ...menuCollapsedStyles,
          transition: 'opacity .25s ease-in-out'
        }}
      />
    );

  const MenuUnlockedIcon = () =>
    userMenuUnlockedIcon || (
      <CircleOutline
        sx={{
          fontSize: '1.25rem',
          pointerEvents: 'none',
          ...menuCollapsedStyles,
          transition: 'opacity .25s ease-in-out'
        }}
      />
    );

  const QuantumLogo = () => {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={40}
        height={40}
        id="Layer_1"
        data-name="Layer 1"
        viewBox="0 0 98.8 70.5">
        <defs>
          <linearGradient
            id="linear-gradient1"
            x1="49.41"
            y1="44.06"
            x2="49.41"
            y2="75.31"
            gradientUnits="userSpaceOnUse">
            <stop offset="1" stopColor="#ff0066" />
            <stop offset="1" stopColor="#ff0066" />
          </linearGradient>
          <linearGradient
            id="linear-gradient-4"
            x1="49.41"
            y1="3.9"
            x2="49.41"
            y2="117.19"
            xlinkHref="#linear-gradient1"
          />
        </defs>{' '}
        <g id="Layer_1-2">
          <g>
            <path
              fill="url(#linear-gradient-4)"
              d="m91.15,41.78c0,10.48-8.51,18.99-18.99,18.99h-2.15c-2.11,0-3.81-1.71-3.81-3.81s1.6-3.7,3.62-3.79h2.34c6.26,0,11.39-5.13,11.39-11.39,0-5.89-4.63-10.86-10.56-11.24l-5.81-.42-1.14-5.7c-1.93-9.76-10.52-16.82-20.47-16.82-7.82,0-14.88,4.33-18.49,11.28l-1.9,3.61-4.06.41c-7.71.8-13.52,7.3-13.52,15.08s5.3,13.58,12.32,14.91v.02c1.82.29,3.21,1.86,3.21,3.75,0,2.1-1.71,3.8-3.82,3.8h-.07c-.1,0-.21-.01-.31-.02h-.01c-.17-.02-.32-.05-.48-.08,0,0-.02,0-.03-.01C7.93,58.3,0,49.05,0,37.98c0-11.73,8.89-21.42,20.32-22.63C25.07,6.23,34.6,0,45.57,0c13.83,0,25.34,9.84,27.92,22.94,9.87.69,17.66,8.81,17.66,18.84Z"
            />
            <path fill="url(#linear-gradient-4)" d="m70.02,53.16h0s-.2.01-.2.01c.06-.01.13-.01.19-.01h.01Z" />
            <path
              fill="url(#linear-gradient-4)"
              d="m77.45,37.09c-1.43-1.65-3.26-2.55-5.49-2.72-2.19-.16-4.13.48-5.8,1.9-1.67,1.42-2.59,3.26-2.75,5.49l-.02.28-8.67,2.32c-.73-1.55-1.68-2.77-2.83-3.65-1.15-.88-2.44-1.53-3.86-1.96l.65-9.02c1.81-.38,3.31-1.28,4.49-2.7,1.18-1.41,1.83-2.93,1.94-4.56.17-2.24-.46-4.18-1.89-5.83-1.43-1.65-3.26-2.56-5.49-2.72-2.19-.16-4.13.47-5.8,1.9-1.67,1.42-2.59,3.25-2.75,5.49-.12,1.63.29,3.23,1.23,4.8.94,1.56,2.3,2.67,4.08,3.32l-.66,9.01c-1.46.22-2.82.69-4.07,1.39-1.24.71-2.34,1.78-3.29,3.21l-8.31-3.56.02-.28c.16-2.24-.47-4.18-1.89-5.83-1.43-1.65-3.26-2.55-5.5-2.72-2.19-.16-4.12.48-5.79,1.9-1.68,1.43-2.59,3.26-2.76,5.49-.16,2.19.49,4.13,1.94,5.8,1.44,1.67,3.26,2.59,5.45,2.75,1.49.11,2.83-.12,4.02-.69s2.18-1.41,2.96-2.53l8.25,3.48c-.34,1.48-.33,2.92.04,4.33.07.27.15.54.24.8.13.41.29.81.46,1.19.3.64.65,1.25,1.06,1.83l-5.36,5.77-1.12,1.2c-.5-.27-.98-.48-1.47-.63-.48-.15-1.04-.25-1.7-.3-2.19-.16-4.12.47-5.79,1.9-.52.45-.97.93-1.34,1.46-.83,1.15-1.3,2.49-1.41,4.03-.16,2.19.48,4.12,1.93,5.8,1.45,1.67,3.27,2.59,5.46,2.75,2.23.16,4.18-.48,5.83-1.93s2.55-3.27,2.71-5.46c.07-.93,0-1.77-.2-2.51-.2-.74-.51-1.48-.93-2.21l.43-.47,3.3-3.63,2.69-2.95c1.43.9,3,1.41,4.7,1.53,1.7.12,3.32-.16,4.87-.84l1.56,2.26,2.5,3.63,1.34,1.95c-.51.67-.93,1.36-1.24,2.09-.31.73-.5,1.56-.56,2.49-.16,2.19.48,4.12,1.93,5.8,1.45,1.67,3.27,2.59,5.46,2.75,2.23.16,4.18-.48,5.83-1.93s2.55-3.27,2.71-5.46c.17-2.24-.47-4.18-1.89-5.83-.69-.79-1.47-1.42-2.34-1.86-.94-.49-2-.78-3.16-.86-.65-.05-1.22-.03-1.72.05s-1.01.22-1.54.41l-2.22-3.23-3.18-4.61c.88-.87,1.57-1.86,2.1-2.99.07-.17.15-.35.22-.53.09-.22.17-.44.24-.66.37-1.14.5-2.35.39-3.61l8.67-2.25c.56,1.21,1.41,2.18,2.52,2.92,1.12.74,2.43,1.16,3.92,1.27,2.23.16,4.18-.48,5.83-1.93,1.65-1.45,2.56-3.27,2.72-5.46.16-2.24-.47-4.18-1.9-5.83Zm-54.74,4.42c-.78.67-1.7.96-2.77.89-1.07-.08-1.94-.51-2.61-1.28-.67-.78-.97-1.7-.89-2.77s.5-1.94,1.28-2.61c.77-.67,1.69-.97,2.77-.89,1.07.08,1.94.5,2.61,1.28.67.77.96,1.69.89,2.77-.08,1.07-.51,1.94-1.28,2.61Zm6.22,30.38c-.77.67-1.7.97-2.77.89-1.07-.08-1.94-.5-2.61-1.28-.67-.77-.97-1.69-.89-2.77.08-1.07.51-1.94,1.28-2.61.77-.67,1.7-.96,2.77-.89,1.07.08,1.94.51,2.61,1.28.67.78.97,1.7.89,2.77s-.5,1.94-1.28,2.61Zm15.33-47.51c-.67-.77-.97-1.69-.89-2.77.08-1.07.51-1.94,1.28-2.61.78-.67,1.7-.96,2.77-.89,1.07.08,1.94.51,2.61,1.28.67.78.97,1.7.89,2.77s-.5,1.95-1.28,2.62c-.77.67-1.7.96-2.77.88-1.07-.07-1.94-.5-2.61-1.28Zm7.22,24.88c-.07,1.01-.34,1.92-.8,2.72-.23.42-.52.82-.86,1.19-.15.16-.31.31-.48.46-1.31,1.15-2.82,1.66-4.54,1.54-1.69-.12-3.09-.79-4.21-2-.06-.06-.12-.12-.17-.18-.28-.32-.52-.66-.72-1.01-.63-1.08-.9-2.26-.81-3.57.13-1.77.86-3.22,2.18-4.34,1.32-1.12,2.85-1.62,4.57-1.49,1.77.13,3.22.84,4.34,2.14,1.13,1.3,1.63,2.81,1.5,4.54Zm6.85,19.36c.77-.67,1.7-.96,2.77-.89,1.07.08,1.94.51,2.61,1.28.67.78.97,1.7.89,2.77s-.5,1.94-1.28,2.61c-.77.67-1.7.97-2.77.89s-1.94-.5-2.61-1.28c-.67-.77-.97-1.69-.89-2.77.08-1.07.5-1.94,1.28-2.61Zm15.54-23.39c-.77.67-1.7.96-2.77.89-1.07-.08-1.94-.51-2.61-1.28-.67-.78-.97-1.7-.89-2.77.08-1.07.51-1.95,1.28-2.62.78-.67,1.7-.96,2.77-.88,1.07.07,1.94.5,2.61,1.28.67.77.97,1.69.89,2.77-.08,1.07-.5,1.94-1.28,2.61Z"
            />
            <path fill="url(#linear-gradient-4)" d="m70.21,53.17h-.39c.06-.01.13-.01.19-.01h.01c.06,0,.13,0,.19.01Z" />
          </g>
        </g>
      </svg>
    );
  };

  const MyPortalIcon = () => {
    return (
      <svg id="Layer_2" xmlns="http://www.w3.org/2000/svg" width={30} height={30} viewBox="0 0 18.92 18.88">
        <defs>
          <style>{'.cls-1{fill:#21D0B2;stroke-width:0px;}'}</style>
        </defs>
        <g id="Layer_1-2">
          <path
            className="cls-1"
            d="M9.46,18.88C4.24,18.88,0,14.65,0,9.44S4.24,0,9.46,0s9.46,4.24,9.46,9.44-4.24,9.44-9.46,9.44ZM9.46,1.34C4.98,1.34,1.34,4.97,1.34,9.44s3.64,8.1,8.12,8.1,8.12-3.64,8.12-8.1S13.93,1.34,9.46,1.34Z"
          />
          <path
            className="cls-1"
            d="M9.46,15.83c-3.53,0-6.4-2.86-6.4-6.39s2.87-6.39,6.4-6.39,6.4,2.86,6.4,6.39-2.87,6.39-6.4,6.39ZM9.46,4.39c-2.79,0-5.06,2.26-5.06,5.05s2.27,5.05,5.06,5.05,5.06-2.26,5.06-5.05-2.27-5.05-5.06-5.05Z"
          />
          <path
            className="cls-1"
            d="M9.46,12.77c-1.84,0-3.33-1.49-3.33-3.33s1.5-3.33,3.33-3.33,3.33,1.49,3.33,3.33-1.5,3.33-3.33,3.33ZM9.46,7.45c-1.1,0-2,.89-2,1.99s.9,1.99,2,1.99,1.99-.89,1.99-1.99-.89-1.99-1.99-1.99Z"
          />
        </g>
      </svg>
    );
  };

  const MyPortalLogo = () => {
    return (
      <svg id="Layer_2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 56.06 17.11" width={160} height={40}>
        <defs>
          <style>
            {'.cls-1{isolation:isolate;}.cls-2{fill:#a0a6c5;}.cls-2,.cls-3{stroke-width:0px;}.cls-3{fill:#fff;}'}
          </style>
        </defs>
        <g id="Layer_1-2">
          <g id="myPortal_Driving_insight_to_your_orders" className="cls-1">
            <g className="cls-1">
              <path
                className="cls-3"
                d="M9.98,7.89c.4.52.59,1.2.59,2.05v3.96c0,.12-.04.22-.12.3s-.18.12-.3.12-.22-.04-.3-.12c-.08-.08-.12-.18-.12-.3v-3.91c0-.62-.15-1.12-.44-1.5-.29-.39-.75-.58-1.37-.58-.39,0-.76.09-1.11.28-.35.19-.63.44-.85.76-.21.32-.32.67-.32,1.04v3.91c0,.12-.04.22-.12.3-.08.08-.18.12-.3.12s-.22-.04-.3-.12c-.08-.08-.12-.18-.12-.3v-3.96c0-.61-.14-1.1-.42-1.47-.28-.38-.72-.57-1.32-.57-.38,0-.74.09-1.08.28-.34.19-.62.44-.83.75-.21.31-.32.65-.32,1.01v3.96c0,.12-.04.22-.12.3s-.18.12-.3.12-.22-.04-.3-.12c-.08-.08-.12-.18-.12-.3v-6.26c0-.12.04-.22.12-.3s.18-.12.3-.12.22.04.3.12c.08.08.12.18.12.3v.81c.25-.38.59-.7,1.02-.95s.88-.38,1.36-.38c.54,0,1.01.13,1.4.39s.67.64.84,1.15c.2-.41.56-.77,1.08-1.08.52-.31,1.05-.46,1.58-.46.84,0,1.46.26,1.85.78Z"
              />
              <path
                className="cls-3"
                d="M18.24,7.16c.08.08.12.18.12.3v6.16c0,.74-.14,1.36-.43,1.89-.29.52-.69.92-1.2,1.19-.51.27-1.09.41-1.74.41-.39,0-.76-.04-1.1-.13-.34-.08-.62-.2-.85-.34-.2-.12-.29-.26-.29-.41,0-.02,0-.07.03-.14.03-.09.07-.16.14-.21.06-.05.14-.07.21-.07.06,0,.11.01.17.04.6.33,1.17.49,1.71.49.77,0,1.39-.24,1.84-.73s.68-1.15.68-1.98v-.49c-.23.39-.57.71-1.01.94-.44.24-.92.36-1.43.36-.81,0-1.43-.24-1.85-.73s-.63-1.14-.63-1.97v-4.29c0-.12.04-.22.12-.3s.18-.12.3-.12.22.04.3.12c.08.08.12.18.12.3v4.17c0,.67.15,1.18.44,1.52.29.35.75.52,1.38.52.38,0,.75-.1,1.1-.29.35-.19.63-.44.84-.75.21-.31.31-.64.31-1v-4.17c0-.12.04-.22.12-.3s.18-.12.3-.12.22.04.3.12Z"
              />
              <path
                className="cls-3"
                d="M25.78,5.58c.41.24.73.57.96.99.23.42.34.9.34,1.43s-.11,1.02-.34,1.45c-.23.44-.55.78-.96,1.03s-.87.38-1.39.38h-2.9v3.02c0,.12-.04.22-.12.3s-.18.12-.3.12-.22-.04-.3-.12c-.08-.08-.12-.18-.12-.3V5.65c0-.12.04-.22.12-.3s.18-.12.3-.12h3.32c.51,0,.98.12,1.39.36ZM25.71,9.48c.34-.39.5-.88.5-1.47s-.17-1.05-.5-1.41c-.34-.36-.77-.55-1.32-.55h-2.9v4.01h2.9c.54,0,.98-.2,1.32-.59Z"
              />
              <path
                className="cls-3"
                d="M34.61,12.64c-.31.57-.73,1.01-1.27,1.34-.54.32-1.14.48-1.81.48s-1.26-.16-1.81-.48c-.54-.32-.97-.77-1.27-1.34-.31-.57-.46-1.2-.46-1.9s.15-1.33.46-1.9c.31-.57.73-1.02,1.27-1.34.54-.33,1.14-.49,1.81-.49s1.26.16,1.81.49c.54.33.97.77,1.27,1.34s.46,1.2.46,1.9-.15,1.33-.46,1.9ZM33.89,9.22c-.23-.45-.56-.8-.97-1.05-.42-.26-.88-.38-1.38-.38s-.96.13-1.37.38-.73.61-.97,1.05c-.24.45-.36.95-.36,1.51s.12,1.05.36,1.5c.24.45.56.8.97,1.06s.87.38,1.37.38.96-.13,1.38-.38c.42-.25.74-.6.97-1.05.23-.45.35-.95.35-1.51s-.12-1.06-.35-1.51Z"
              />
              <path
                className="cls-3"
                d="M40.59,7.11c.15.07.22.19.22.34,0,.13-.04.24-.11.31-.07.08-.16.12-.27.12-.05,0-.14-.02-.27-.07-.14-.05-.26-.07-.38-.07-.35,0-.69.13-1.03.4-.34.27-.62.61-.84,1.03s-.33.84-.33,1.27v3.45c0,.12-.04.22-.12.3s-.18.12-.3.12-.22-.04-.3-.12c-.08-.08-.12-.18-.12-.3v-6.26c0-.12.04-.22.12-.3s.18-.12.3-.12.22.04.3.12c.08.08.12.18.12.3v1.23c.21-.53.54-.97.97-1.32.43-.35.94-.53,1.53-.54.19,0,.35.04.5.11Z"
              />
              <path
                className="cls-3"
                d="M43.92,7.98v4.68c0,.35.07.59.2.72.13.13.3.19.52.19.06,0,.13-.01.21-.03s.15-.04.2-.04c.08,0,.16.04.22.1.06.07.09.15.09.24,0,.13-.07.24-.22.33s-.33.13-.53.13c-.25,0-.48-.02-.67-.07-.2-.05-.38-.19-.57-.42-.18-.23-.27-.61-.27-1.12v-4.72h-1.02c-.11,0-.21-.04-.29-.11s-.12-.17-.12-.28.04-.2.12-.28c.08-.07.18-.11.29-.11h1.02v-1.55c0-.12.04-.22.12-.3s.18-.12.3-.12.22.04.3.12c.08.08.12.18.12.3v1.55h1.4c.1,0,.19.04.27.13.08.08.12.18.12.28,0,.11-.04.2-.11.27-.07.07-.17.1-.28.1h-1.4Z"
              />
              <path
                className="cls-3"
                d="M52.98,7.3c.08.08.12.19.12.31v6.29c0,.12-.04.22-.12.3-.08.08-.18.12-.3.12s-.22-.04-.3-.12c-.08-.08-.12-.18-.12-.3v-.94c-.23.42-.58.77-1.05,1.06-.47.29-.98.43-1.53.43-.63,0-1.19-.16-1.69-.49-.5-.33-.89-.77-1.18-1.34-.29-.57-.43-1.21-.43-1.91s.14-1.34.43-1.91c.29-.56.68-1,1.18-1.32.5-.32,1.05-.47,1.66-.47.56,0,1.07.14,1.54.41.47.27.82.62,1.05,1.06v-.87c0-.12.04-.22.12-.31.08-.08.18-.13.3-.13s.22.04.3.13ZM51.07,13.29c.39-.26.69-.61.91-1.06.22-.45.33-.96.33-1.52s-.11-1.03-.33-1.48c-.22-.45-.52-.8-.91-1.05s-.82-.38-1.31-.38-.92.13-1.31.38-.69.6-.91,1.04c-.22.44-.33.94-.33,1.5s.11,1.06.32,1.52.52.81.9,1.06c.39.26.83.38,1.32.38s.92-.13,1.31-.38Z"
              />
              <path
                className="cls-3"
                d="M55.95,14.19c-.08.08-.18.12-.3.12s-.22-.04-.3-.12c-.08-.08-.12-.18-.12-.3V5.44c0-.12.04-.22.12-.3s.18-.12.3-.12.22.04.3.12c.08.08.12.18.12.3v8.46c0,.12-.04.22-.12.3Z"
              />
            </g>
          </g>
          <path
            className="cls-2"
            d="M1.45,2.62h-.61v1.1H.03V.05h1.5c.87,0,1.4.52,1.4,1.27h0c0,.86-.66,1.3-1.48,1.3ZM2.12,1.33c0-.36-.25-.55-.65-.55h-.62v1.12h.64c.4,0,.64-.24.64-.55h0Z"
          />
          <path
            className="cls-2"
            d="M5.19,3.77c-.98,0-1.59-.55-1.59-1.63V.05h.81v2.07c0,.6.3.91.79.91s.79-.3.79-.88V.05h.81v2.07c0,1.11-.62,1.65-1.61,1.65Z"
          />
          <path className="cls-2" d="M7.58,3.72V.05h.81v2.93h1.83v.73h-2.63Z" />
          <path
            className="cls-2"
            d="M12.36,3.77c-.55,0-1.11-.19-1.55-.59l.48-.57c.33.27.68.45,1.09.45.33,0,.53-.13.53-.35h0c0-.21-.13-.32-.74-.48-.74-.19-1.21-.39-1.21-1.12h0c0-.67.53-1.11,1.28-1.11.53,0,.99.17,1.36.47l-.42.61c-.32-.23-.64-.36-.95-.36s-.47.14-.47.32h0c0,.25.16.33.79.49.74.19,1.16.46,1.16,1.1h0c0,.74-.55,1.15-1.35,1.15Z"
          />
          <path className="cls-2" d="M14.38,3.72V.05h2.76v.72h-1.96v.74h1.73v.72h-1.73v.77h1.99v.72h-2.79Z" />
        </g>
      </svg>
    );
  };

  const FlowLogo = () => {
    return (
      <svg id="Layer_2" xmlns="http://www.w3.org/2000/svg" viewBox="-6 0 70 20" width={120} height={50}>
        <defs>
          <style>{'.cls-1{fill:#a0a6c5;}.cls-1,.cls-2{stroke-width:0px;}.cls-2{fill:#fff;}'}</style>
        </defs>
        <g id="Layer_1-2">
          <path
            className="cls-1"
            d="M1.15,20.1c-.3-.13-.58-.33-.86-.6-.06-.05-.1-.12-.1-.19,0-.06.03-.12.08-.18.05-.05.11-.08.17-.08s.12.03.18.08c.42.46.94.69,1.56.69.38,0,.7-.09.96-.27.25-.18.38-.42.38-.71,0-.23-.06-.42-.19-.57s-.29-.28-.49-.37c-.2-.1-.46-.19-.78-.3-.34-.1-.63-.21-.86-.34s-.42-.29-.56-.49-.22-.46-.22-.78c0-.28.08-.53.23-.75.15-.22.37-.39.64-.52.27-.13.59-.19.95-.19.33,0,.63.05.92.16.29.11.51.27.68.47.08.09.12.17.12.23,0,.06-.03.11-.08.16-.05.05-.11.08-.18.08-.05,0-.1-.02-.14-.06-.14-.17-.34-.31-.58-.42-.24-.11-.49-.16-.75-.16-.39,0-.71.09-.95.26s-.37.41-.37.71.12.55.35.71c.23.17.57.32,1.01.46.38.11.69.23.93.34.24.12.44.28.59.5.15.22.23.5.23.85,0,.27-.08.52-.24.74-.16.22-.38.4-.66.52-.28.13-.6.19-.96.19-.38,0-.72-.06-1.02-.19Z"
          />
          <path
            className="cls-1"
            d="M10.84,16.54c.23.3.34.69.34,1.17v2.26c0,.07-.02.13-.07.17s-.1.07-.17.07-.13-.02-.17-.07c-.05-.05-.07-.1-.07-.17v-2.24c0-.35-.08-.64-.25-.86-.17-.22-.43-.33-.78-.33-.22,0-.44.05-.64.16-.2.11-.36.25-.48.44-.12.18-.18.38-.18.59v2.24c0,.07-.02.13-.07.17s-.1.07-.17.07-.13-.02-.17-.07-.07-.1-.07-.17v-2.26c0-.35-.08-.63-.24-.84-.16-.22-.41-.32-.75-.32-.22,0-.43.05-.62.16-.19.11-.35.25-.47.43-.12.18-.18.37-.18.58v2.26c0,.07-.02.13-.07.17s-.1.07-.17.07-.13-.02-.17-.07c-.05-.05-.07-.1-.07-.17v-3.58c0-.07.02-.13.07-.17s.1-.07.17-.07.13.02.17.07c.05.05.07.1.07.17v.46c.14-.22.34-.4.58-.54.25-.14.5-.22.78-.22.31,0,.58.07.8.22s.38.37.48.65c.11-.23.32-.44.62-.61.3-.18.6-.26.9-.26.48,0,.83.15,1.06.44Z"
          />
          <path
            className="cls-1"
            d="M16.03,16.2s.07.11.07.18v3.59c0,.07-.02.13-.07.17s-.1.07-.17.07-.13-.02-.17-.07c-.04-.05-.07-.1-.07-.17v-.54c-.13.24-.33.44-.6.61-.27.17-.56.25-.87.25-.36,0-.68-.09-.96-.28s-.51-.44-.67-.77-.24-.69-.24-1.09.08-.77.25-1.09.39-.57.68-.75c.29-.18.6-.27.95-.27.32,0,.61.08.88.23.27.15.47.36.6.61v-.5c0-.07.02-.13.07-.18.05-.05.1-.07.17-.07s.13.02.17.07ZM14.94,19.62c.22-.15.39-.35.52-.61.12-.26.19-.55.19-.87s-.06-.59-.19-.85c-.13-.26-.3-.46-.52-.6-.22-.15-.47-.22-.75-.22s-.53.07-.75.22c-.22.14-.4.34-.52.59-.13.25-.19.54-.19.86s.06.61.18.87c.12.26.29.46.52.61.22.15.47.22.76.22s.53-.07.75-.22Z"
          />
          <path
            className="cls-1"
            d="M19.65,16.09c.09.04.13.11.13.19,0,.07-.02.13-.06.18-.04.05-.09.07-.15.07-.03,0-.08-.01-.16-.04-.08-.03-.15-.04-.22-.04-.2,0-.39.08-.59.23-.19.15-.35.35-.48.59-.12.24-.19.48-.19.73v1.97c0,.07-.02.13-.07.17s-.1.07-.17.07-.13-.02-.17-.07c-.05-.05-.07-.1-.07-.17v-3.58c0-.07.02-.13.07-.17s.1-.07.17-.07.13.02.17.07c.05.05.07.1.07.17v.7c.12-.3.31-.56.56-.75s.54-.3.88-.31c.11,0,.2.02.29.06Z"
          />
          <path
            className="cls-1"
            d="M21.68,16.59v2.68c0,.2.04.34.11.41s.17.11.3.11c.03,0,.07,0,.12-.02.05-.01.09-.02.11-.02.05,0,.09.02.12.06s.05.09.05.14c0,.07-.04.14-.13.19-.09.05-.19.08-.3.08-.14,0-.27-.01-.38-.04-.11-.03-.22-.11-.32-.24-.1-.13-.16-.35-.16-.64v-2.7h-.58c-.06,0-.12-.02-.16-.06-.05-.04-.07-.1-.07-.16s.02-.12.07-.16c.04-.04.1-.06.16-.06h.58v-.89c0-.07.02-.13.07-.17.05-.05.1-.07.17-.07s.13.02.17.07c.05.04.07.1.07.17v.89h.8c.06,0,.11.02.16.07.04.05.07.1.07.16,0,.06-.02.12-.06.16-.04.04-.1.06-.16.06h-.8Z"
          />
          <path
            className="cls-1"
            d="M29.16,19.8s.07.1.07.17-.02.13-.07.17-.1.07-.17.07h-2.86c-.07,0-.13-.02-.17-.07s-.07-.1-.07-.17v-5.11c0-.07.02-.13.07-.17.05-.05.11-.07.18-.07.07,0,.13.02.18.07s.07.1.07.17v4.88h2.59c.07,0,.13.02.17.06Z"
          />
          <path
            className="cls-1"
            d="M33.51,19.25c-.18.32-.42.58-.73.76-.31.18-.65.28-1.03.28s-.72-.09-1.03-.28c-.31-.18-.55-.44-.73-.76s-.26-.69-.26-1.09.09-.76.26-1.09.42-.58.73-.77c.31-.19.65-.28,1.03-.28s.72.09,1.03.28c.31.19.55.44.73.77.18.32.26.69.26,1.09s-.09.76-.26,1.09ZM33.09,17.3c-.13-.26-.32-.46-.56-.6s-.5-.22-.79-.22-.55.07-.78.22c-.23.15-.42.35-.56.6-.14.26-.2.54-.2.86s.07.6.2.85c.14.26.32.46.56.6.23.15.5.22.78.22s.55-.07.79-.22c.24-.14.42-.34.56-.6.13-.26.2-.54.2-.86s-.07-.61-.2-.86Z"
          />
          <path
            className="cls-1"
            d="M38.52,16.19s.07.11.07.18v3.46c0,.42-.09.77-.26,1.07s-.41.52-.71.67-.65.23-1.04.23c-.19,0-.38-.02-.59-.07-.21-.05-.38-.11-.53-.18-.15-.07-.23-.15-.25-.23-.04-.04-.06-.08-.06-.13,0-.05.02-.1.07-.13.02-.02.05-.02.08-.02s.09.02.18.06l.17.07c.33.13.64.19.93.19.47,0,.85-.13,1.12-.4.27-.27.4-.63.4-1.09v-.5c-.13.29-.34.51-.63.68-.29.16-.59.24-.9.24-.37,0-.7-.09-1-.28s-.53-.44-.7-.76c-.17-.32-.26-.69-.26-1.09s.09-.76.26-1.09c.17-.32.4-.58.7-.76.3-.18.63-.28.99-.28.34,0,.64.08.92.25.28.17.49.37.62.6v-.51c0-.07.02-.13.07-.18.05-.05.1-.07.17-.07s.13.02.17.07ZM37.4,19.63c.23-.14.41-.34.54-.6.13-.26.2-.55.2-.87s-.07-.61-.2-.87c-.13-.26-.31-.45-.54-.6-.23-.14-.5-.21-.79-.21s-.55.07-.78.22c-.23.15-.41.35-.54.6-.13.26-.2.54-.2.85s.07.61.2.86c.13.26.31.46.54.6.23.15.49.22.78.22s.56-.07.79-.21Z"
          />
          <path
            className="cls-1"
            d="M39.92,15.44c-.06-.05-.09-.12-.09-.22v-.08c0-.1.03-.17.09-.22.06-.05.15-.08.25-.08.21,0,.32.1.32.3v.08c0,.1-.03.17-.08.22-.06.05-.14.08-.24.08s-.19-.03-.25-.08ZM40.34,20.14s-.1.07-.17.07-.13-.02-.17-.07-.07-.1-.07-.17v-3.67c0-.07.02-.13.07-.17.05-.04.1-.07.17-.07s.13.02.17.07c.05.05.07.1.07.17v3.67c0,.07-.02.13-.07.17Z"
          />
          <path
            className="cls-1"
            d="M41.45,19.38c0-.07.03-.12.1-.16.03-.03.07-.05.13-.05.06,0,.12.03.18.08.31.42.73.62,1.26.62.26,0,.48-.06.67-.18.19-.12.28-.29.28-.52s-.09-.39-.26-.5-.42-.21-.74-.29c-.44-.11-.79-.26-1.04-.44-.25-.18-.38-.44-.38-.79,0-.22.06-.41.18-.59.12-.17.29-.31.5-.4.21-.1.45-.14.72-.14.23,0,.46.04.69.13s.43.23.6.42c.05.04.07.09.07.16,0,.06-.03.12-.08.17-.04.03-.08.05-.14.05-.05,0-.1-.02-.14-.06-.13-.14-.28-.25-.46-.33-.18-.07-.37-.11-.57-.11-.25,0-.47.06-.64.18s-.27.29-.27.53c.01.21.11.38.29.49.18.11.45.21.81.3.28.07.51.15.69.23.18.09.33.21.44.36.11.15.17.36.17.62,0,.35-.14.62-.42.83s-.62.31-1.03.31c-.65,0-1.17-.24-1.55-.73-.04-.07-.06-.13-.06-.18Z"
          />
          <path
            className="cls-1"
            d="M46.47,16.59v2.68c0,.2.04.34.11.41s.17.11.3.11c.03,0,.07,0,.12-.02.05-.01.09-.02.11-.02.05,0,.09.02.12.06s.05.09.05.14c0,.07-.04.14-.13.19-.09.05-.19.08-.3.08-.14,0-.27-.01-.38-.04-.11-.03-.22-.11-.32-.24-.1-.13-.16-.35-.16-.64v-2.7h-.58c-.06,0-.12-.02-.16-.06-.05-.04-.07-.1-.07-.16s.02-.12.07-.16c.04-.04.1-.06.16-.06h.58v-.89c0-.07.02-.13.07-.17.05-.05.1-.07.17-.07s.13.02.17.07c.05.04.07.1.07.17v.89h.8c.06,0,.11.02.16.07.04.05.07.1.07.16,0,.06-.02.12-.06.16-.04.04-.1.06-.16.06h-.8Z"
          />
          <path
            className="cls-1"
            d="M48.43,15.44c-.06-.05-.09-.12-.09-.22v-.08c0-.1.03-.17.09-.22.06-.05.15-.08.25-.08.21,0,.32.1.32.3v.08c0,.1-.03.17-.08.22-.06.05-.14.08-.24.08s-.19-.03-.25-.08ZM48.85,20.14s-.1.07-.17.07-.13-.02-.17-.07-.07-.1-.07-.17v-3.67c0-.07.02-.13.07-.17.05-.04.1-.07.17-.07s.13.02.17.07c.05.05.07.1.07.17v3.67c0,.07-.02.13-.07.17Z"
          />
          <path
            className="cls-1"
            d="M52.74,16.11c.22.06.39.13.52.23.13.1.2.2.2.31,0,.06-.02.11-.06.16s-.09.07-.15.07c-.05,0-.1-.01-.13-.03s-.07-.05-.12-.1c-.07-.07-.14-.12-.2-.15-.08-.04-.19-.07-.32-.09-.13-.02-.27-.04-.42-.04-.29,0-.56.07-.8.22s-.43.35-.57.61c-.14.26-.21.54-.21.85s.07.61.2.86c.13.26.32.46.56.6.24.15.51.22.81.22.19,0,.35-.02.46-.05.11-.03.23-.08.34-.14.04-.02.1-.07.19-.14.04-.04.08-.07.11-.08.03-.02.07-.03.1-.03.06,0,.11.02.15.06.04.04.06.09.06.16,0,.09-.06.19-.19.29-.13.1-.3.19-.53.27-.22.07-.47.11-.74.11-.39,0-.74-.09-1.04-.28-.3-.18-.54-.44-.71-.76s-.25-.69-.25-1.09.09-.74.26-1.07c.17-.32.41-.58.72-.77.31-.19.66-.29,1.06-.29.25,0,.48.03.7.08Z"
          />
          <path
            className="cls-1"
            d="M54.27,19.38c0-.07.03-.12.1-.16.03-.03.07-.05.13-.05.06,0,.12.03.18.08.31.42.73.62,1.26.62.26,0,.48-.06.67-.18.19-.12.28-.29.28-.52s-.09-.39-.26-.5-.42-.21-.74-.29c-.44-.11-.79-.26-1.04-.44-.25-.18-.38-.44-.38-.79,0-.22.06-.41.18-.59.12-.17.29-.31.5-.4.21-.1.45-.14.72-.14.23,0,.46.04.69.13s.43.23.6.42c.05.04.07.09.07.16,0,.06-.03.12-.08.17-.04.03-.08.05-.14.05-.05,0-.1-.02-.14-.06-.13-.14-.28-.25-.46-.33-.18-.07-.37-.11-.57-.11-.25,0-.47.06-.64.18s-.27.29-.27.53c.01.21.11.38.29.49.18.11.45.21.81.3.28.07.51.15.69.23.18.09.33.21.44.36.11.15.17.36.17.62,0,.35-.14.62-.42.83s-.62.31-1.03.31c-.65,0-1.17-.24-1.55-.73-.04-.07-.06-.13-.06-.18Z"
          />
          <path className="cls-2" d="M2.46,2.43v2.38h5.35v2.24H2.46v4.33H0V.19h8.53v2.24H2.46Z" />
          <path className="cls-2" d="M10.5,11.38V.19h2.46v8.95h5.59v2.24h-8.05Z" />
          <path
            className="cls-2"
            d="M25.56,11.57c-3.46,0-5.94-2.57-5.94-5.75v-.03c0-3.18,2.51-5.78,5.97-5.78s5.94,2.57,5.94,5.75v.03c0,3.18-2.51,5.78-5.97,5.78ZM28.96,5.78c0-1.92-1.41-3.51-3.39-3.51s-3.36,1.57-3.36,3.48v.03c0,1.92,1.41,3.52,3.39,3.52s3.36-1.57,3.36-3.48v-.03Z"
          />
          <path
            className="cls-2"
            d="M45.51,11.46h-2.14l-2.53-7.32-2.53,7.32h-2.15L32.34.19h2.64l2.32,7.57L39.82.16h2.11l2.51,7.6L46.76.19h2.58l-3.83,11.26Z"
          />
        </g>
      </svg>
    );
  };

  const FlowIcon = () => {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={35}
        height={35}
        id="Layer_1"
        data-name="Layer 1"
        viewBox="0 0 81 70">
        <defs>
          <linearGradient
            id="linear-gradient1"
            x1="49.41"
            y1="44.06"
            x2="49.41"
            y2="75.31"
            gradientUnits="userSpaceOnUse">
            <stop offset="1" stopColor="#009fe3" />
            <stop offset="1" stopColor="#009fe3" />
          </linearGradient>
          <linearGradient
            id="linear-gradient-3"
            x1="49.41"
            y1="3.9"
            x2="49.41"
            y2="117.19"
            xlinkHref="#linear-gradient1"
          />
        </defs>{' '}
        <path
          fill="url(#linear-gradient-3)"
          d="M19.4,76.3c-2.6,0-4.8-0.9-6.7-2.8c-1.9-1.9-2.8-4.1-2.8-6.7c0-2.7,0.9-4.9,2.8-6.7c1.9-1.8,4.1-2.7,6.7-2.7
  c0.8,0,1.5,0.1,2,0.2c0.6,0.1,1.2,0.3,1.8,0.6l7-8.8c-1-1.3-1.9-2.7-2.4-4.4c-0.6-1.6-0.7-3.3-0.4-5.1l-10-3.4
  c-0.8,1.4-1.9,2.5-3.3,3.2c-1.4,0.8-2.9,1.2-4.7,1.2c-2.6,0-4.8-0.9-6.7-2.8C0.9,36.3,0,34.1,0,31.5c0-2.7,0.9-4.9,2.8-6.7
  C4.6,23,6.9,22,9.4,22c2.7,0,4.9,0.9,6.7,2.7c1.8,1.8,2.7,4.1,2.7,6.7v0.3L29,35.3c1-1.8,2.2-3.1,3.6-4.1c1.4-0.9,3-1.6,4.7-2V18.6
  c-2.2-0.6-3.9-1.8-5.1-3.6c-1.2-1.8-1.9-3.6-1.9-5.6c0-2.7,0.9-4.9,2.8-6.7C35,0.9,37.2,0,39.8,0c2.7,0,4.9,0.9,6.7,2.7
  s2.7,4.1,2.7,6.7c0,1.9-0.6,3.8-1.9,5.6c-1.3,1.8-3,3-5.1,3.6v10.7c1.7,0.4,3.3,1,4.7,2c1.4,0.9,2.7,2.3,3.6,4.1l10-3.5v-0.3
  c0-2.7,0.9-4.9,2.8-6.7c1.9-1.8,4.1-2.7,6.7-2.7c2.7,0,4.9,0.9,6.7,2.7c1.8,1.8,2.7,4.1,2.7,6.7c0,2.6-0.9,4.8-2.7,6.7
  c-1.8,1.9-4.1,2.8-6.7,2.8c-1.8,0-3.3-0.4-4.7-1.2c-1.4-0.8-2.5-1.9-3.2-3.2l-10,3.4c0.3,1.8,0.2,3.5-0.4,5.1
  c-0.5,1.6-1.3,3.1-2.4,4.4l7,8.8c0.6-0.3,1.2-0.5,1.8-0.6c0.6-0.1,1.3-0.2,2-0.2c2.7,0,4.9,0.9,6.7,2.7s2.7,4.1,2.7,6.7
  c0,2.6-0.9,4.8-2.7,6.7c-1.8,1.9-4.1,2.8-6.7,2.8c-2.6,0-4.8-0.9-6.7-2.8c-1.9-1.9-2.8-4.1-2.8-6.7c0-1.1,0.2-2.1,0.5-3
  s0.7-1.7,1.3-2.6l-7-8.8c-1.8,0.9-3.7,1.4-5.7,1.4c-2,0-3.9-0.5-5.7-1.4l-7,8.9c0.6,0.8,1,1.7,1.3,2.5c0.3,0.9,0.5,1.8,0.5,2.9
  c0,2.6-0.9,4.8-2.7,6.7C24.3,75.3,22,76.3,19.4,76.3z M9.4,36c1.3,0,2.3-0.4,3.2-1.3c0.9-0.9,1.3-1.9,1.3-3.2c0-1.3-0.4-2.3-1.3-3.2
  S10.7,27,9.4,27s-2.3,0.4-3.2,1.3C5.4,29.2,5,30.2,5,31.5c0,1.3,0.4,2.3,1.3,3.2C7.1,35.5,8.2,36,9.4,36z M19.4,71.3
  c1.3,0,2.3-0.4,3.2-1.3c0.9-0.9,1.3-1.9,1.3-3.2c0-1.3-0.4-2.3-1.3-3.2c-0.9-0.9-1.9-1.3-3.2-1.3c-1.3,0-2.3,0.4-3.2,1.3
  c-0.9,0.9-1.3,1.9-1.3,3.2c0,1.3,0.4,2.3,1.3,3.2C17.1,70.9,18.1,71.3,19.4,71.3z M39.8,13.9c1.3,0,2.3-0.4,3.2-1.3
  c0.9-0.9,1.3-1.9,1.3-3.2S43.8,7.1,43,6.3C42.1,5.4,41.1,5,39.8,5c-1.3,0-2.3,0.4-3.2,1.3c-0.9,0.9-1.3,1.9-1.3,3.2s0.4,2.3,1.3,3.2
  C37.5,13.5,38.5,13.9,39.8,13.9z M39.9,48.9c2,0,3.8-0.7,5.2-2.2c1.4-1.5,2.2-3.2,2.2-5.3c0-2-0.7-3.8-2.2-5.2
  c-1.4-1.4-3.2-2.2-5.3-2.2c-2,0-3.8,0.7-5.3,2.2c-1.5,1.4-2.2,3.2-2.2,5.3c0,2,0.7,3.8,2.2,5.3C36,48.2,37.8,48.9,39.9,48.9z
   M60.2,71.3c1.3,0,2.3-0.4,3.2-1.3c0.9-0.9,1.3-1.9,1.3-3.2s-0.4-2.3-1.3-3.2c-0.9-0.9-1.9-1.3-3.2-1.3c-1.3,0-2.3,0.4-3.2,1.3
  c-0.9,0.9-1.3,1.9-1.3,3.2c0,1.3,0.4,2.3,1.3,3.2C57.8,70.9,58.9,71.3,60.2,71.3z M70.1,36c1.3,0,2.3-0.4,3.2-1.3
  c0.9-0.9,1.3-1.9,1.3-3.2c0-1.3-0.4-2.3-1.3-3.2c-0.9-0.9-1.9-1.3-3.2-1.3s-2.3,0.4-3.2,1.3c-0.9,0.9-1.3,1.9-1.3,3.2
  c0,1.3,0.4,2.3,1.3,3.2S68.9,36,70.1,36z"
        />
      </svg>
    );
  };

  const FusionLogo = () => {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={45}
        height={40}
        id="Layer_1"
        data-name="Layer 1"
        viewBox="0 0 98.8 70.5">
        <defs>
          <linearGradient
            id="linear-gradient"
            x1="49.41"
            y1="44.06"
            x2="49.41"
            y2="75.31"
            gradientUnits="userSpaceOnUse">
            <stop offset="1" stopColor="#C04CFD" />
            <stop offset="1" stopColor="#C04CFD" />
          </linearGradient>
          <linearGradient
            id="linear-gradient-2"
            x1="49.41"
            y1="3.9"
            x2="49.41"
            y2="117.19"
            xlinkHref="#linear-gradient"
          />
        </defs>{' '}
        <path
          fill="url(#linear-gradient-2)"
          d="M55.1,70.67c-3.12,0-6.58-.63-10.31-1.9-8.6-2.93-17.77-8.98-25.84-17.05C1.49,34.27-4.98,13.4,4.21,4.21,8.87-.45,
          16.57-1.27,25.88,1.9c8.6,2.93,17.77,8.98,25.84,17.05,8.06,8.06,14.12,17.24,17.05,25.84,3.17,9.31,2.35,17.01-2.31,
          21.67h0c-2.8,2.8-6.68,4.21-11.36,4.21ZM15.61,6.01c-3.06,0-5.53,.82-7.16,2.44-5.86,5.86-1.34,22.95,14.74,39.02,7.43,
          7.43,15.79,12.97,23.53,15.61,6.93,2.36,12.58,2.04,15.49-.87h0c2.92-2.92,3.23-8.56,.87-15.49-2.64-7.75-8.18-16.1-15.61-23.53-7.43-7.43-15.79-12.97-23.53-15.61-3.07-1.05-5.89-1.57-8.34-1.57Z"
        />
        <path
          fill="url(#linear-gradient-2)"
          d="M15.57,70.67c-4.67,0-8.56-1.42-11.36-4.21h0s0,0,0,0C-.45,61.79-1.27,54.1,1.9,44.79c2.93-8.6,8.98-17.77,17.05-25.84C27.01,10.88,36.19,4.83,44.79,1.9c9.31-3.17,17.01-2.35,
          21.67,2.31,9.19,9.19,2.72,30.06-14.74,47.51-8.06,8.06-17.24,14.12-25.84,17.05-3.73,1.27-7.2,1.9-10.31,1.9ZM55.06,6.01c-2.44,0-5.26,.52-8.34,1.57-7.75,2.64-16.1,8.18-23.53,15.61-7.43,7.43-12.97,
          15.79-15.61,23.53-2.36,6.93-2.04,12.58,.87,15.49h0c2.92,2.92,8.56,3.23,15.49,.87,7.75-2.64,16.1-8.18,23.53-15.61,16.08-16.08,20.6-33.16,14.74-39.02-1.62-1.62-4.09-2.44-7.16-2.44Z"
        />
        <path
          fill="url(#linear-gradient-2)"
          d="M35.33,27.87c4.12,0,7.46,3.34,7.46,7.46s-3.34,7.46-7.46,7.46-7.46-3.34-7.46-7.46,3.34-7.46,7.46-7.46m0-5c-6.87,0-12.46,5.59-12.46,12.46s5.59,12.46,12.46,12.46,12.46-5.59,12.46-12.46-5.59-12.46-12.46-12.46h0Z"
        />
      </svg>
    );
  };

  useEffect(() => {
    const favicon = getFaviconEl();

    switch (prod) {
      case Products.Fusion:
        document.title = 'Fusion';
        if (favicon) {
          favicon.href = '/images/products/fusion.svg';
        }

        break;

      case Products.Flow:
        document.title = 'Flow';
        if (favicon) {
          favicon.href = '/images/products/flowicon.svg';
        }

        break;

      case Products.MyPortal:
        document.title = 'My Portal';
        if (favicon) {
          favicon.href = '/images/products/pulseicon.svg';
        }

        break;

      default:
        document.title = 'Quantum';
        if (favicon) {
          favicon.href = '/images/products/quantum.svg';
        }

        break;
    }
  });

  return (
    <MenuHeaderWrapper className="nav-header" sx={{ pl: menuHeaderPaddingLeft() }}>
      {userVerticalNavMenuBranding ? (
        userVerticalNavMenuBranding(props)
      ) : (
        <Link href="/" passHref legacyBehavior>
          <StyledLink>
            {prod === Products.Quantum && <QuantumLogo></QuantumLogo>}
            {prod === Products.Fusion && <FusionLogo></FusionLogo>}
            {prod === Products.Flow && (
              <>
                <FlowIcon></FlowIcon>
                <FlowLogo></FlowLogo>
              </>
            )}
            {prod === Products.MyPortal && (
              <>
                <MyPortalIcon></MyPortalIcon>
                <MyPortalLogo></MyPortalLogo>
              </>
            )}

            {prod === Products.Quantum ||
              (prod === Products.Fusion && (
                <VigoTypography
                  value={title()}
                  disableTranslation
                  style={{
                    fontWeight: 900,
                    lineHeight: 'normal',
                    textTransform: 'uppercase',
                    transition: 'opacity .25s ease-in-out, margin .25s ease-in-out',
                    fontSize: '2.0rem',
                    fontFamily: 'Gotham Rounded',
                    letterSpacing: '0rem',
                    marginLeft: '0px',
                    marginTop: '0px',
                    maxWidth: '186px',
                    textOverflow: 'none !important',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap'
                  }}
                  sx={{ ...menuCollapsedStyles, ...(navCollapsed && !navHover ? {} : { ml: 1 }) }}
                />
              ))}
          </StyledLink>
        </Link>
      )}
      {hidden ? (
        <IconButton
          disableRipple
          disableFocusRipple
          onClick={toggleNavVisibility}
          sx={{ padding: 0, backgroundColor: 'transparent !important' }}>
          <Close fontSize="small" />
        </IconButton>
      ) : (
        <>
          <IconButton
            disableRipple
            disableFocusRipple
            onClick={() => saveSettings({ ...settings, navCollapsed: !navCollapsed })}
            sx={{
              padding: 0,
              marginLeft: '5px',
              marginTop: '0px',
              color: 'text.primary',
              backgroundColor: 'transparent !important'
            }}>
            {navCollapsed ? MenuUnlockedIcon() : MenuLockedIcon()}
          </IconButton>
        </>
      )}
    </MenuHeaderWrapper>
  );
};

export default VerticalNavHeader;
